/** XeAgents.ai**/
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight } from "lucide-react";
import { Agent } from "./useCaseData";

interface AgentCardProps {
  agent: Agent;
  index: number;
}

export function AgentCard({ agent, index }: AgentCardProps) {
  const IconComponent = agent.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className="flex flex-col h-full group"
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="relative bg-white dark:bg-slate-900/80 rounded-2xl border border-slate-200/80 dark:border-slate-700/50 overflow-hidden transition-all duration-500 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 h-full flex flex-col">
        {/* Top visual area */}
        <div
          className={`${agent.bgColor} relative p-5 sm:p-6 overflow-hidden`}
        >
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.08]">
            <svg className="w-full h-full" viewBox="0 0 200 200">
              <defs>
                <pattern
                  id={`dots-${agent.id}`}
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="2" cy="2" r="1" fill="currentColor" />
                </pattern>
              </defs>
              <rect
                width="200"
                height="200"
                fill={`url(#dots-${agent.id})`}
              />
            </svg>
          </div>

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white dark:bg-slate-800 shadow-md flex items-center justify-center group-hover:scale-110 group-hover:shadow-lg transition-all duration-500">
                <IconComponent
                  className={`w-6 h-6 sm:w-7 sm:h-7 ${agent.iconColor}`}
                />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  AI Powered
                </span>
              </div>
            </div>

            <motion.div
              className="opacity-0 group-hover:opacity-100 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
            >
              <div className="h-8 w-8 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm flex items-center justify-center shadow-sm">
                <ArrowUpRight className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content area */}
        <div className="p-5 sm:p-6 flex flex-col flex-1">
          <h3
            className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white mb-2"
            data-testid={`agent-title-${agent.id}`}
          >
            {agent.title}
          </h3>

          <div className="flex flex-wrap gap-1.5 mb-3">
            {agent.categories.slice(0, 3).map((category) => (
              <Badge
                key={category}
                variant="secondary"
                className="text-[10px] sm:text-xs font-normal px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-0"
                data-testid={`agent-category-${agent.id}-${category
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {category}
              </Badge>
            ))}
          </div>

          <p
            className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed flex-1"
            data-testid={`agent-description-${agent.id}`}
          >
            {agent.description}
          </p>
        </div>

        {/* Bottom gradient accent */}
        <div
          className={`h-[2px] bg-gradient-to-r ${
            agent.iconColor.includes("violet")
              ? "from-violet-500 to-purple-500"
              : agent.iconColor.includes("blue")
                ? "from-blue-500 to-cyan-500"
                : agent.iconColor.includes("emerald")
                  ? "from-emerald-500 to-green-500"
                  : agent.iconColor.includes("amber")
                    ? "from-amber-500 to-orange-500"
                    : agent.iconColor.includes("green")
                      ? "from-green-500 to-emerald-500"
                      : agent.iconColor.includes("indigo")
                        ? "from-indigo-500 to-violet-500"
                        : agent.iconColor.includes("cyan")
                          ? "from-cyan-500 to-blue-500"
                          : agent.iconColor.includes("rose")
                            ? "from-rose-500 to-pink-500"
                            : agent.iconColor.includes("purple")
                              ? "from-purple-500 to-violet-500"
                              : agent.iconColor.includes("orange")
                                ? "from-orange-500 to-amber-500"
                                : agent.iconColor.includes("pink")
                                  ? "from-pink-500 to-rose-500"
                                  : agent.iconColor.includes("teal")
                                    ? "from-teal-500 to-emerald-500"
                                    : "from-slate-500 to-slate-400"
          } opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
        />
      </div>
    </motion.div>
  );
}